<template>
    <v-dialog v-model="show" no-click-animation persistent width="600">
        <div class="AgentDictionary">
            <!--Header dialog-->
            <div class="dialog-header">
                <div>{{ $f.tc('dictionary') }}</div>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <!--Content dialog-->
            <div class="dialog-content fci-scrollbar" ref="contentDialog">
                <table>
                    <thead>
                    <tr>
                        <th>{{ $f.tc('acronym') }}</th>
                        <th>{{ $f.tc('pronoun') }}</th>
                        <th v-if="isEdit"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, i) in itemList" :key="i">
                        <td>
                            <v-text-field
                                outlined
                                v-model="item.acronym"
                                class="input-custom"
                                style="width: 100%"
                                :readonly="!isEdit"
                            ></v-text-field>
                        </td>
                        <td>
                            <v-text-field
                                outlined
                                v-model="item.pronoun"
                                class="input-custom"
                                style="width: 100%"
                                :readonly="!isEdit"
                            ></v-text-field>
                        </td>
                        <td v-if="isEdit">
                            <v-btn icon class="mx-0" @click.stop="deleteItem(i)">
                                <img src="/img/delete-blue.svg" alt="icon-delete"
                                     style="transform: scale(1.3); padding-left: 1px">
                            </v-btn>
                        </td>
                    </tr>
                    <!--New variable-->
                    <tr v-if="isEdit">
                        <td>
                            <v-text-field
                                outlined
                                v-model="acronym"
                                class="input-custom"
                                @keypress.enter="addItem"
                                :placeholder="$f.tc('acronym')"
                                style="width: 100%"
                                ref="NewKeyVariable"
                            ></v-text-field>
                        </td>
                        <td>
                            <v-text-field
                                outlined
                                v-model="pronoun"
                                class="input-custom"
                                @keypress.enter="addItem"
                                :placeholder="$f.tc('pronoun')"
                                style="width: 100%"
                            ></v-text-field>
                        </td>
                        <td>
                            <v-btn icon class="mx-0" @click.stop="addItem">
                                <img src="/img/add-blue.svg" alt="icon-delete" style="transform: scale(1.4);">
                            </v-btn>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <!--Footer dialog-->
            <div class="dialog-footer" v-if="isEdit">
                <div class="btn-custom"
                     @click="saveList">{{ $f.tc('save') }}
                </div>
                <div class="btn-outline" @click="close">{{ $f.tc('cancel') }}</div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import {mapState} from "vuex";
import lodash from "lodash";
import AgentService from "@/services/agent.service";
import DialogPromiseMixin from "@/modules/fptai/mixins/DialogPromiseMixin.vue";

export default {
    name: "AgentDictionary",
    mixins: [DialogPromiseMixin],
    data() {
        return {
            acronym: '',
            pronoun: '',
            itemList: [],
            originalItemMap: [],
        }
    },
    computed: {
        ...mapState({
            dictionarySetting: state => state.agent.dictionary
        }),
        isEdit() {
            return AgentService.isEditable()
        },
    },
    methods: {
        loadDictionarySettingList() {
            this.itemList = lodash.orderBy(this.$utils.cloneObject(this.dictionarySetting.value), 'acronym')
            this.originalItemMap = this.loadOriginalItemMap()
        },
        loadOriginalItemMap() {
            let map = []
            for (let item of this.dictionarySetting.value) {
                map.push({
                    acronym: item.acronym,
                    pronoun: item.pronoun
                })
            }
            return map
        },
        open() {
            this.acronym = ''
            this.pronoun = ''
            this.show = true
            this.loadDictionarySettingList()
            return new Promise((resolve, reject) => {
                this.promise = {resolve, reject}
            })
        },
        addItem() {
            if (!this.acronym.trim()) {
                this.$appService.toastError(this.$f.tc('itemFieldRequired', {item: this.$f.tc('acronym')}))
                return
            }
            if (!this.pronoun.trim()) {
                this.$appService.toastError(this.$f.tc('itemFieldRequired', {item: this.$f.tc('pronoun')}))
                return
            }
            if (this.nameIsExist()) {
                this.$appService.toastError(this.$f.tc('fieldItemDuplicate', {
                    key: this.$f.tc('acronym'),
                    value: this.acronym
                }))
                return
            }
            if(this.acronym.length > 256 ) {
                this.$appService.toastError(this.$f.tc('itemFieldTooLong', {item: this.$f.tc('acronym')}))
                return
            }
            if(this.pronoun.length > 256 ) {
                this.$appService.toastError(this.$f.tc('itemFieldTooLong', {item: this.$f.tc('pronoun')}))
                return
            }

            this.itemList.push({
                acronym: this.acronym.trim(),
                pronoun: this.pronoun.trim(),
            })
            this.scrollBottom()
            this.acronym = ''
            this.pronoun = ''
            this.$refs.NewKeyVariable.$el.focus()
        },
        nameIsExist() {
            let item = this.itemList.find(item => item.acronym === this.acronym)
            return !!item
        },
        deleteItem(index) {
            this.itemList.splice(index, 1)
            this.$forceUpdate()
        },
        saveList() {
            if (this.validateItemList() && !lodash.isEqual(this.itemList, this.originalItemMap)) {
                this.trimItemList()
                this.$store.dispatch(this.$actionTypes.agent.UPDATE_DICTIONARY, {
                    id: this.dictionarySetting.id,
                    value: this.itemList
                }).then(() => {
                    this.close()
                })
            }
        },
        validateItemList() {
            // check empty
            for (let item of this.itemList) {
                for (let key in item) {
                    if (!item[key].trim()) {
                        this.$appService.toastError(this.$f.tc('fieldItemEmpty'))
                        return false
                    }
                }
            }

            // check duplicate
            let set = new Set(this.itemList.flatMap(i => i.acronym))
            if (set.size !== this.itemList.length) {
                this.$appService.toastError(this.$f.tc('fieldItemDuplicateExist', {key: this.$f.tc('acronym')}))
                return false
            }

            return true
        },
        scrollBottom() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.$refs.contentDialog.scrollTo({
                        top: this.$refs.contentDialog.scrollHeight
                    })
                })
            })
        },
        trimItemList() {
            this.itemList.map(function (item) {
                for (let key in item) {
                    item[key] = item[key].trim()
                }
            })
        }
    }
}
</script>

<style scoped lang="scss">
.AgentDictionary {
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .dialog-content {
        padding: 12px;
        max-height: calc(85vh - 100px);
        overflow-y: auto;
    }

    .btn-custom {
        margin-right: 8px;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}


table {
    border: 1px solid #96add0;
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    tr {
        height: 48px;
    }

    thead tr:not(.v-datatable__progress) {
        height: 48px !important;
    }

    thead th {
        text-transform: capitalize !important;
        color: #12274F !important;
        background: #E4EDFA;
        text-align: left;
    }

    th:nth-child(1) {
        padding-left: 12px;
    }

    th:nth-child(2) {
        padding-left: 6px;
    }

    tr {
        border-bottom: 1px solid #96add0;
    }

    tr {
        td {
            padding: 0 6px;

            &:first-child {
                padding-left: 12px;
            }
        }
    }
}

.error-message {
    color: red;
    margin-top: 5px;
    font-size: 13px;
    font-family: "open sans", serif;
}

::v-deep {
    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
        position: relative;
    }

    .v-text-field__details {
        position: absolute;
        right: 12px;
        top: 8px
    }

    .v-input--selection-controls {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }
}
</style>
